import * as React from "react";
import PropTypes from "prop-types";
import {
  useInfluencerCharacters,
  useCharacters,
  useDebounce,
} from "../../../hooks";
import { Button, IconButton, TextInput, DeleteIcon } from "../../../themeV2";
import css from "./AdvancedSettings.module.css";

const toEntry = (c) => ({
  franchise_character_id: c.franchise_character_id ?? null,
  title_character_id: c.title_character_id ?? null,
});

export default function CharacterAssociations({ influencerId, triggerAlert }) {
  const { characters: currentCharacters, refresh: refreshCharacters } =
    useInfluencerCharacters(influencerId);

  const [searchOpen, setSearchOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  useDebounce(() => setDebouncedQuery(searchQuery), 300, [searchQuery]);

  const { characters: searchResults } = useCharacters({
    q: debouncedQuery.length >= 2 ? debouncedQuery : null,
  });

  const currentFranchiseIds = React.useMemo(
    () =>
      new Set(
        currentCharacters.map((c) => c.franchise_character_id).filter(Boolean),
      ),
    [currentCharacters],
  );

  const filteredResults = React.useMemo(
    () =>
      searchResults.filter(
        (c) => !currentFranchiseIds.has(c.franchise_character_id),
      ),
    [searchResults, currentFranchiseIds],
  );

  const syncCharacters = React.useCallback(
    async (entries) => {
      setSaving(true);
      try {
        const res = await window.fetch(
          `/api/influencer/${influencerId}/characters`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ characters: entries }),
          },
        );
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(
            body?.message || body?.error || `Request failed (${res.status})`,
          );
        }
        refreshCharacters();
        triggerAlert({ message: "Characters updated" });
      } catch (err) {
        triggerAlert({ message: err.message, severity: "error" });
      }
      setSaving(false);
    },
    [influencerId, refreshCharacters, triggerAlert],
  );

  const handleAdd = React.useCallback(
    (franchiseCharacterId) => {
      const entries = [
        ...currentCharacters.map(toEntry),
        { franchise_character_id: franchiseCharacterId },
      ];
      syncCharacters(entries);
      setSearchQuery("");
      setDebouncedQuery("");
    },
    [currentCharacters, syncCharacters],
  );

  const handleRemove = React.useCallback(
    (influencerCharacterId) => {
      const entries = currentCharacters
        .filter((c) => c.influencer_character_id !== influencerCharacterId)
        .map(toEntry);
      syncCharacters(entries);
    },
    [currentCharacters, syncCharacters],
  );

  return (
    <div>
      <p className={css.caption}>
        {currentCharacters.length} character
        {currentCharacters.length !== 1 ? "s" : ""} assigned
      </p>

      {currentCharacters.length > 0 && (
        <div style={{ marginBlockStart: "var(--space-2)" }}>
          {currentCharacters.map((character) => (
            <div
              className={css.accessRow}
              key={character.influencer_character_id}
            >
              <div>
                <p
                  style={{
                    font: "var(--type-body-lg)",
                    letterSpacing: "var(--tracking-body-lg)",
                    margin: 0,
                  }}
                >
                  {character.character_name}
                </p>
                <p className={css.caption} style={{ margin: 0 }}>
                  {character.franchise_name}
                </p>
              </div>
              <IconButton
                onClick={() => handleRemove(character.influencer_character_id)}
                disabled={saving}
                aria-label={`Remove ${character.character_name}`}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginBlockStart: "var(--space-2)" }}>
        <Button
          variant="outlined"
          onClick={() => setSearchOpen((prev) => !prev)}
        >
          {searchOpen ? "Cancel" : "Add Character"}
        </Button>
      </div>

      {searchOpen && (
        <div className={css.searchPanel}>
          <TextInput
            label="Search by character name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            description="Type to search franchise characters"
            autoFocus
          />

          {saving && <p className={css.caption}>Saving...</p>}

          {filteredResults.length > 0 && (
            <ul className={css.resultList}>
              {filteredResults.map((character) => (
                <li
                  key={character.franchise_character_id}
                  className={css.resultItem}
                  role="button"
                  tabIndex={0}
                  aria-disabled={saving}
                  onClick={() => handleAdd(character.franchise_character_id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleAdd(character.franchise_character_id);
                    }
                  }}
                >
                  <div>
                    <span>{character.name}</span>
                    <p
                      className={css.resultItemSecondary}
                      style={{ margin: 0 }}
                    >
                      {character.franchise_name}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {debouncedQuery.length >= 2 &&
            !saving &&
            filteredResults.length === 0 && (
              <p className={css.caption}>No matching characters found</p>
            )}
        </div>
      )}
    </div>
  );
}

CharacterAssociations.propTypes = {
  influencerId: PropTypes.number.isRequired,
  triggerAlert: PropTypes.func.isRequired,
};
